import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(next, { replace: true });
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + (next.startsWith("/") ? next : "/");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto px-4 md:px-8 py-16">
      <p className="text-xs font-semibold uppercase tracking-wider text-blue-600 mb-2">Sign in</p>
      <h1 className="font-bold text-4xl text-slate-900 mt-1 mb-2">Welcome back.</h1>
      <p className="text-sm text-slate-500 mb-8">
        New here?{" "}
        <Link to={`/signup${next !== "/" ? `?next=${encodeURIComponent(next)}` : ""}`} className="text-blue-600 hover:underline" data-testid="link-signup">
          Create an account
        </Link>
      </p>

      <button
        onClick={googleLogin}
        className="w-full bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 hover:border-slate-400 font-semibold py-3.5 text-sm rounded-lg transition-all shadow-sm hover:shadow inline-flex items-center justify-center gap-3 mb-5 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
        data-testid="google-login-button"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <path d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0786-1.7959 2.7167v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z" fill="#4285F4"/>
          <path d="M9 18c2.43 0 4.4673-.806 5.9564-2.1804l-2.9087-2.258c-.8059.54-1.8368.859-3.0477.859-2.344 0-4.328-1.5836-5.036-3.7118H.9574v2.3318C2.4382 15.9832 5.4818 18 9 18z" fill="#34A853"/>
          <path d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2822-1.71V4.9582H.9574C.3477 6.173 0 7.5482 0 9s.3477 2.827.9574 4.0418L3.964 10.71z" fill="#FBBC05"/>
          <path d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4627.8918 11.4255 0 9 0 5.4818 0 2.4382 2.0168.9574 4.9582L3.964 7.29C4.672 5.1618 6.656 3.5795 9 3.5795z" fill="#EA4335"/>
        </svg>
        Continue with Google
      </button>

      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1 h-px bg-slate-200" />
        <span className="text-[10px] font-medium uppercase tracking-wider text-slate-400">OR EMAIL</span>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Email
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 px-3 py-3 text-sm rounded-lg text-slate-900"
            data-testid="login-email-input"
            autoComplete="email"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Password
            </label>
            <Link
              to="/forgot-password"
              className="text-xs text-slate-500 hover:text-blue-600 transition-colors"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </Link>
          </div>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 px-3 py-3 text-sm rounded-lg text-slate-900"
            data-testid="login-password-input"
            autoComplete="current-password"
          />
        </div>

        {err && (
          <div className="border border-red-200 bg-red-50 p-3 text-xs text-red-600 rounded-lg" data-testid="login-error">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 text-sm rounded-lg transition-colors inline-flex items-center justify-center gap-2"
          data-testid="login-submit-button"
        >
          {busy ? "Signing in…" : "Sign in"} <ArrowRight size={14} weight="bold" />
        </button>
      </form>
    </div>
  );
}
